import React, { useState } from 'react';
import { useUser } from '../../context/UserContext';
import { SmileIcon, Wind, PuzzleIcon, Music, HeartIcon, RefreshCwIcon, CloudIcon, BrainIcon, PencilIcon, BookOpenIcon } from 'lucide-react';
import WritingEditor from '../common/WritingEditor';
// Enhanced garden games with value learning objectives
const games = [{
  id: 'mood-match',
  title: 'Mood Match',
  description: 'Match emoji faces to mood words. Improve emotional awareness.',
  learningValue: 'Wisdom',
  valueLesson: 'Recognizing emotions is the first step to responding wisely to them.',
  icon: SmileIcon,
  color: 'bg-yellow-100',
  iconColor: 'text-yellow-600',
  difficulty: 'Easy'
}, {
  id: 'breath-ball',
  title: 'Breath Ball',
  description: 'Guide a floating ball with your breath. Practice mindful breathing.',
  learningValue: 'Wisdom',
  valueLesson: "Mindfulness develops the wisdom to respond rather than react to life's challenges.",
  icon: Wind,
  color: 'bg-blue-100',
  iconColor: 'text-blue-600',
  difficulty: 'Easy',
  isWritingActivity: true,
  writingPrompt: 'After practicing mindful breathing, reflect on your experience. How did your mind and body respond? What did you notice about your thoughts during the exercise? How might you incorporate mindful breathing into your daily life?'
}, {
  id: 'values-sorting',
  title: 'Values Sorting',
  description: "Drag and drop value cards into 'most important'/'less important'.",
  learningValue: 'Truth',
  valueLesson: 'Clarifying your values helps you live in alignment with your deepest truths.',
  icon: HeartIcon,
  color: 'bg-pink-100',
  iconColor: 'text-pink-600',
  difficulty: 'Medium',
  isWritingActivity: true,
  writingPrompt: 'eflect on your core values. Which values did you identify as most important to you? Why do these particular values resonate with you? How do they show up in your daily life? Are there areas where you like to better align your actions with your values'
}, {
  id: 'garden-puzzle',
  title: 'Garden Puzzle',
  description: 'Assemble a nature-themed puzzle to unlock garden milestones.',
  learningValue: 'Patience',
  valueLesson: 'Cultivating your garden of virtues requires patience and consistent effort.',
  icon: PuzzleIcon,
  color: 'bg-green-100',
  iconColor: 'text-green-600',
  difficulty: 'Medium'
}, {
  id: 'affirmation-builder',
  title: 'Affirmation Builder',
  description: 'Create and save/share a personalized affirmation.',
  learningValue: 'Love',
  valueLesson: 'Self-compassion and positive self-talk nurture self-love and resilience.',
  icon: RefreshCwIcon,
  color: 'bg-purple-100',
  iconColor: 'text-purple-600',
  difficulty: 'Easy',
  isWritingActivity: true,
  writingPrompt: 'reate a personal affirmation that resonates with you. What words of encouragement do you need to hear right now? How can you phrase this affirmation to be positive, in the present tense, and meaningful? How might this affirmation support your personal growth'
}, {
  id: 'reflection-spinner',
  title: 'Reflection Spinner',
  description: 'Spin for a random reflection prompt or micro-challenge.',
  learningValue: 'Honesty',
  valueLesson: 'Honest self-reflection is the foundation for meaningful personal growth.',
  icon: RefreshCwIcon,
  color: 'bg-indigo-100',
  iconColor: 'text-indigo-600',
  difficulty: 'Easy',
  isWritingActivity: true,
  writingPrompt: "espond to today's reflection prompt: 'Describe a moment when you were completely honest with yourself about something difficult. What made this honesty possible? What resulted from this moment of truth?"
}, {
  id: 'soundscape-escape',
  title: 'Soundscape Escape',
  description: 'Match nature sounds to gardens. Create your own peaceful soundscape.',
  learningValue: 'Respect',
  valueLesson: 'Creating harmony in your environment reflects and cultivates inner harmony.',
  icon: Music,
  color: 'bg-cyan-100',
  iconColor: 'text-cyan-600',
  difficulty: 'Medium'
}, {
  id: 'bravery-scenarios',
  title: 'Bravery Scenarios',
  description: 'Face virtual challenges and practice courage in a safe environment.',
  learningValue: 'Bravery',
  valueLesson: 'Courage grows when we practice facing fears in small, manageable steps.',
  icon: BrainIcon,
  color: 'bg-orange-100',
  iconColor: 'text-orange-600',
  difficulty: 'Medium',
  isWritingActivity: true,
  writingPrompt: 'Reflect on a scenario that challenged your courage. What fears came up for you? What small steps could you take to build your courage in this area? What would becoming more brave in this area allow in your life?'
}, {
  id: 'humility-heroes',
  title: 'Humility Heroes',
  description: 'Learn about historical figures who demonstrated great humility despite achievements.',
  learningValue: 'Humility',
  valueLesson: 'True greatness often comes from those who serve others without seeking recognition.',
  icon: BrainIcon,
  color: 'bg-green-100',
  iconColor: 'text-green-600',
  difficulty: 'Easy'
}];
const GardenGames: React.FC = () => {
  const {
    saveWrittenResponse,
    getWrittenResponse
  } = useUser();
  const [selectedGame, setSelectedGame] = useState<string | null>(null);
  const [showLesson, setShowLesson] = useState<Record<string, boolean>>({});
  const [writingActivity, setWritingActivity] = useState<{
    activityId: string;
    promptTitle: string;
    promptText: string;
    initialContent: string;
  } | null>(null);
  const handleGameSelect = (gameId: string) => {
    setSelectedGame(gameId);
    // In a real app, this would launch the game
    setTimeout(() => {
      setSelectedGame(null);
    }, 1000);
  };
  const toggleLesson = (gameId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent triggering the parent div onClick
    setShowLesson(prev => ({
      ...prev,
      [gameId]: !prev[gameId]
    }));
  };
  const openWritingEditor = (game: any, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent triggering the parent div onClick
    const activityId = `game-${game.id}`;
    const existingResponse = getWrittenResponse(activityId);
    setWritingActivity({
      activityId,
      promptTitle: game.title,
      promptText: game.writingPrompt,
      initialContent: existingResponse?.content || ''
    });
  };
  const handleSaveWriting = (content: string) => {
    if (writingActivity) {
      saveWrittenResponse(writingActivity.activityId, content, writingActivity.promptTitle, writingActivity.promptText);
      setWritingActivity(null);
    }
  };
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-420">
      {games.map(game => {
      const Icon = game.icon;
      const isSelected = selectedGame === game.id;
      const isLessonVisible = showLesson[game.id];
      const isWritingActivity = game.isWritingActivity;
      const activityId = `game-${game.id}`;
      const hasWrittenResponse = getWrittenResponse(activityId);
      return <div key={game.id} onClick={() => handleGameSelect(game.id)} className={`${game.color} rounded-xl shadow-md p-5 cursor-pointer transition-transform ${isSelected ? 'scale-95' : 'hover:scale-105'}`} data-id="element-421">
            <div className="flex items-center mb-3 justify-between" data-id="element-422">
              <div className="flex items-center" data-id="element-423">
                <div className="bg-white p-2 rounded-full mr-3" data-id="element-424">
                  <Icon className={game.iconColor} size={20} data-id="element-425" />
                </div>
                <h3 className="text-lg font-medium" data-id="element-426">{game.title}</h3>
              </div>
              <span className="bg-white text-xs font-medium px-2 py-0.5 rounded-full" data-id="element-427">
                {game.learningValue}
              </span>
            </div>
            <p className="text-gray-700 text-sm mb-3" data-id="element-428">{game.description}</p>
            {/* Show badge if user has written response */}
            {isWritingActivity && hasWrittenResponse && <div className="mb-3 bg-white text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full inline-flex items-center" data-id="element-429">
                <BookOpenIcon size={12} className="mr-1" data-id="element-430" />
                Response saved
              </div>}
            {isLessonVisible && <div className="bg-white bg-opacity-80 p-3 rounded-md mb-3 text-sm" data-id="element-431">
                <strong data-id="element-432">Value Lesson:</strong> {game.valueLesson}
              </div>}
            <div className="flex justify-between items-center" data-id="element-433">
              <span className={`text-xs font-medium px-2.5 py-0.5 rounded bg-white ${game.difficulty === 'Easy' ? 'text-green-800' : game.difficulty === 'Medium' ? 'text-yellow-800' : 'text-red-800'}`} data-id="element-434">
                {game.difficulty}
              </span>
              <div data-id="element-435">
                <button onClick={e => toggleLesson(game.id, e)} className="text-xs underline mr-2" data-id="element-436">
                  {isLessonVisible ? 'Hide lesson' : 'Show lesson'}
                </button>
                {isWritingActivity && <button onClick={e => openWritingEditor(game, e)} className="text-xs bg-white py-1 px-2 rounded-lg shadow-sm mr-2 flex items-center" data-id="element-437">
                    <PencilIcon size={12} className="mr-1" data-id="element-438" />
                    {hasWrittenResponse ? 'Edit' : 'Write'}
                  </button>}
                <button className="text-sm bg-white py-1 px-3 rounded-lg shadow-sm" data-id="element-439">
                  Play
                </button>
              </div>
            </div>
          </div>;
    })}
      {/* Writing Editor Modal */}
      {writingActivity && <WritingEditor initialContent={writingActivity.initialContent} promptTitle={writingActivity.promptTitle} promptText={writingActivity.promptText} onSave={handleSaveWriting} onClose={() => setWritingActivity(null)} activityId={writingActivity.activityId} data-id="element-440" />}
    </div>;
};
export default GardenGames;